import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { copy } from "@/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full border-b border-dealer-ivory/20 bg-transparent py-4 font-sans text-sm font-light text-dealer-ivory placeholder:text-dealer-subtle/60 focus:border-dealer-burgundybright focus:outline-none transition-colors duration-500";

export const Contact = ({ lang = "en" }) => {
  const t = copy[lang].contact;
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "", website: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
    } catch (err) {
      toast.error(t.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative border-t border-dealer-ivory/10 bg-dealer-surface/40 py-28 sm:py-40"
      data-testid="contact-section"
    >
      <div className="mx-auto max-w-[1600px] px-6 sm:px-10">
        <div className="grid gap-20 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="dealer-eyebrow">CONTACT</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-8 font-serif text-4xl leading-[1.1] tracking-tight text-dealer-ivory sm:text-5xl lg:text-6xl">
                LET&rsquo;S
                <br />
                TALK.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-10 max-w-md text-sm font-light leading-relaxed text-dealer-silver sm:text-base">
                {t.sub}
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <a
                href="mailto:asu@dealermgt.com"
                className="mt-8 inline-block font-sans text-[11px] font-medium tracking-[0.25em] text-dealer-silver transition-colors duration-300 hover:text-dealer-ivory"
                data-testid="contact-email-link"
              >
                ASU@DEALERMGT.COM
              </a>
            </Reveal>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            {sent ? (
              <Reveal>
                <div
                  className="flex h-full min-h-[320px] flex-col justify-center border border-dealer-ivory/15 px-8 py-14 sm:px-14"
                  data-testid="contact-success-state"
                >
                  <p className="font-serif text-3xl text-dealer-ivory sm:text-4xl">
                    {t.successTitle}
                  </p>
                  <span className="mt-6 block h-px w-16 bg-dealer-burgundybright" />
                  <p className="mt-6 max-w-sm text-sm font-light leading-relaxed text-dealer-silver">
                    {t.successBody}
                  </p>
                </div>
              </Reveal>
            ) : (
              <Reveal delay={0.15}>
                <form
                  onSubmit={onSubmit}
                  className="space-y-9"
                  data-testid="contact-form"
                >
                  <div className="grid gap-9 sm:grid-cols-2">
                    <div>
                      <label htmlFor="name" className="dealer-eyebrow mb-1 block">
                        {t.name}
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={form.name}
                        onChange={onChange}
                        className={inputClass}
                        data-testid="contact-input-name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="dealer-eyebrow mb-1 block">
                        {t.email}
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={form.email}
                        onChange={onChange}
                        className={inputClass}
                        data-testid="contact-input-email"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="company" className="dealer-eyebrow mb-1 block">
                      {t.company}
                    </label>
                    <input
                      id="company"
                      name="company"
                      type="text"
                      value={form.company}
                      onChange={onChange}
                      className={inputClass}
                      data-testid="contact-input-company"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="dealer-eyebrow mb-1 block">
                      {t.message}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={4}
                      value={form.message}
                      onChange={onChange}
                      className={`${inputClass} resize-none`}
                      data-testid="contact-input-message"
                    />
                  </div>
                  <input
                    type="text"
                    name="website"
                    value={form.website}
                    onChange={onChange}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }}
                  />
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="contact-submit-button"
                    className="group inline-flex items-center gap-4 border border-dealer-ivory/25 px-8 py-4 font-sans text-[11px] font-medium tracking-[0.3em] text-dealer-ivory transition-colors duration-500 hover:border-dealer-burgundybright hover:text-dealer-burgundybright disabled:opacity-50"
                  >
                    {sending ? t.sending : t.submit}
                    <span className="block h-px w-8 bg-current transition-[width] duration-500 group-hover:w-12" />
                  </button>
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
